import React from "react";
import { Select, InputNumber, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const MODE_OPTIONS = [
  { value: "rotate", label: "Rotacion" },
  { value: "fixed_morning", label: "Vetëm mëngjes" },
  { value: "fixed_afternoon", label: "Vetëm pasdite" },
  { value: "excluded", label: "Përjashtuar" },
];

const HEAD_TOOLTIPS = {
  mode:
    "Si trajtohet ky rol nga algoritmi (E PËRBASHKËT për të gjithë ekipet):\n" +
    "• Rotacion: punonjësit ndahen mes mëngjesit dhe pasdites sipas drejtësisë;\n" +
    "• Vetëm mëngjes / pasdite: të gjithë caktohen gjithmonë në atë turn;\n" +
    "• Përjashtuar: roli nuk merr pjesë në rotacion.",
  minSeniors:
    "Sa SENIORË (punonjës me >= vite eksperiencë) duhen çdo javë në pasdite për këtë EKIP të zgjedhur.",
  minJuniors:
    "Sa JUNIORË (punonjës me < vite eksperiencë) duhen çdo javë në pasdite për këtë EKIP të zgjedhur.\n" +
    "Mund të vihet pa seniorë kur ekipi nuk ka asnjë.",
  minYearsExperience:
    "Vjetërsia minimale (në vite) për të konsideruar një punonjës 'senior' për këtë rol.\n" +
    "E PËRBASHKËT për të gjithë ekipet. Llogaritet nga data e punësimit.",
};

const UNTEAMED = "_unteamed";

// Read the per-team quotas with backward-compat:
//   1. rule.teamQuotas[team] (new explicit per-team config)
//   2. rule.minSeniorsAfternoonWeek / minJuniorsAfternoonWeek (flat)
//   3. rule.minPerAfternoonWeek (legacy senior-only)
const readQuotas = (rule, team) => {
  const tq = rule?.teamQuotas?.[team];
  if (tq && (tq.minSeniors !== undefined || tq.minJuniors !== undefined)) {
    return {
      minSeniors: Number(tq.minSeniors || 0),
      minJuniors: Number(tq.minJuniors || 0),
    };
  }
  if (
    rule?.minSeniorsAfternoonWeek !== undefined ||
    rule?.minJuniorsAfternoonWeek !== undefined
  ) {
    return {
      minSeniors: Number(rule.minSeniorsAfternoonWeek || 0),
      minJuniors: Number(rule.minJuniorsAfternoonWeek || 0),
    };
  }
  return {
    minSeniors: Number(rule?.minPerAfternoonWeek || 0),
    minJuniors: 0,
  };
};

// Mirror the per-team config into legacy fields so older readers and
// other team tabs see a sensible value. Strategy: keep teamQuotas as
// the source of truth; the legacy global fields are set to the MAX
// across all team tabs (a defensible "summary" if a consumer ignores
// teamQuotas).
const recomputeLegacyMirror = (rule) => {
  const teams = rule.teamQuotas ? Object.keys(rule.teamQuotas) : [];
  let maxSr = 0;
  let maxJr = 0;
  for (const t of teams) {
    const q = rule.teamQuotas[t] || {};
    maxSr = Math.max(maxSr, Number(q.minSeniors || 0));
    maxJr = Math.max(maxJr, Number(q.minJuniors || 0));
  }
  return {
    ...rule,
    minSeniorsAfternoonWeek: maxSr,
    minJuniorsAfternoonWeek: maxJr,
    minPerAfternoonWeek: maxSr + maxJr,
  };
};

function RoleRulesTable({
  departmentRoles = [],
  value = [],
  onChange,
  team = UNTEAMED,
  isDarkMode = false,
}) {
  const byRoleId = new Map(value.map((r) => [r.roleId, r]));

  const merged = departmentRoles.map((role) => {
    const existing = byRoleId.get(role.id) || {};
    const { minSeniors, minJuniors } = readQuotas(existing, team);
    return {
      roleId: role.id,
      roleName: role.name,
      mode: existing.mode || "excluded",
      minYearsExperience: existing.minYearsExperience ?? 0,
      // raw original rule (so we can preserve other teams' quotas on save)
      _original: existing,
      // current team's quotas (for inputs)
      minSeniors,
      minJuniors,
    };
  });

  const patchRow = (roleId, patchObj) => {
    const next = merged.map((r) => {
      if (r.roleId !== roleId) return r;
      const updated = { ...r, ...patchObj };

      const baseRule = {
        ...(r._original || {}),
        roleId: r.roleId,
        roleName: r.roleName,
        mode: updated.mode,
        minYearsExperience: Number(updated.minYearsExperience || 0),
      };

      const nextTeamQuotas = {
        ...(baseRule.teamQuotas || {}),
        [team]: {
          minSeniors: Number(updated.minSeniors || 0),
          minJuniors: Number(updated.minJuniors || 0),
        },
      };

      const merged2 = recomputeLegacyMirror({
        ...baseRule,
        teamQuotas: nextTeamQuotas,
      });
      return { ...updated, _original: merged2 };
    });

    onChange?.(
      next.map((r) => {
        const { _original, ...rest } = r;
        // Persist the full rule (with teamQuotas + legacy mirror)
        // rather than the per-team view shown in the UI.
        return _original || rest;
      })
    );
  };

  return (
    <div className="role-rules-table">
      <div className="role-rules-table__head">
        <span>Roli</span>
        <span>
          Mënyra{" "}
          <Tooltip title={HEAD_TOOLTIPS.mode} styles={{ root: { whiteSpace: "pre-line", maxWidth: 360 } }}>
            <InfoCircleOutlined style={{ color: "#888", marginLeft: 4 }} />
          </Tooltip>
        </span>
        <span>
          Min. SR/javë{" "}
          <Tooltip title={HEAD_TOOLTIPS.minSeniors} styles={{ root: { whiteSpace: "pre-line", maxWidth: 360 } }}>
            <InfoCircleOutlined style={{ color: "#888", marginLeft: 4 }} />
          </Tooltip>
        </span>
        <span>
          Min. JR/javë{" "}
          <Tooltip title={HEAD_TOOLTIPS.minJuniors} styles={{ root: { whiteSpace: "pre-line", maxWidth: 360 } }}>
            <InfoCircleOutlined style={{ color: "#888", marginLeft: 4 }} />
          </Tooltip>
        </span>
        <span>
          Min. vite{" "}
          <Tooltip title={HEAD_TOOLTIPS.minYearsExperience} styles={{ root: { whiteSpace: "pre-line", maxWidth: 360 } }}>
            <InfoCircleOutlined style={{ color: "#888", marginLeft: 4 }} />
          </Tooltip>
        </span>
      </div>

      {merged.map((row) => {
        const isRotate = row.mode === "rotate";
        return (
          <div
            key={row.roleId}
            className={`role-rules-table__row ${
              row.mode === "excluded" ? "role-rules-table__row--disabled" : ""
            }`}
          >
            <span className="role-rules-table__name">{row.roleName}</span>
            <Select
              value={row.mode}
              onChange={(v) => patchRow(row.roleId, { mode: v })}
              options={MODE_OPTIONS}
              style={{ width: "100%" }}
              classNames={{ popup: { root: isDarkMode ? "darkDropdown" : "" } }}
            />
            <InputNumber
              min={0}
              max={20}
              value={row.minSeniors}
              onChange={(v) => patchRow(row.roleId, { minSeniors: Number(v || 0) })}
              disabled={!isRotate}
              style={{ width: "100%" }}
            />
            <InputNumber
              min={0}
              max={20}
              value={row.minJuniors}
              onChange={(v) => patchRow(row.roleId, { minJuniors: Number(v || 0) })}
              disabled={!isRotate}
              style={{ width: "100%" }}
            />
            <InputNumber
              min={0}
              max={40}
              step={0.5}
              value={row.minYearsExperience}
              onChange={(v) =>
                patchRow(row.roleId, { minYearsExperience: Number(v || 0) })
              }
              disabled={!isRotate}
              style={{ width: "100%" }}
            />
          </div>
        );
      })}

      {merged.length === 0 ? (
        <div className="role-rules-table__row">
          <span style={{ gridColumn: "1 / -1", color: "#888" }}>
            Nuk ka role të konfiguruara për këtë departament.
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default RoleRulesTable;
